/**
 * MyRoutesPage - Guard Route Execution UI
 * Mobile-first interface for guards to execute patrol routes
 * Features:
 * - View assigned routes
 * - Start/complete route runs
 * - Check-in/out at stops with geofence validation
 * - Background GPS tracking (60s intervals)
 */
import { formatSydneyTime } from '../utils/timezone';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLocationTracking } from '../lib/useLocationTracking';
import {

  Route,
  MapPin,
  Play,
  CheckCircle2,
  Clock,
  Navigation,
  Loader2,
  AlertTriangle,
  ChevronRight,
  MapPinOff,
  Target,
  Footprints,
  Timer,
  Flag,
  Camera,
  X,
  RefreshCw,
  Compass,
  RadioTower,
  Building2
} from 'lucide-react';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createStopMarker = (index, status) => {
  const colors = {
    pending: '#71717a',
    current: '#3b82f6',
    checkedIn: '#f59e0b',
    completed: '#22c55e',
  };
  const color = colors[status] || colors.pending;
  
  return L.divIcon({
    className: 'custom-stop-marker',
    html: `<div style="
      background-color: ${color};
      color: white;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 14px;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    ">${index + 1}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

// User location marker
const createUserMarker = () => {
  return L.divIcon({
    className: 'user-location-marker',
    html: `<div style="
      background-color: #3b82f6;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 0 0 2px #3b82f6, 0 2px 8px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
};

// Map component to recenter on user location
function MapRecenter({ position }) {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom());
    }
  }, [position, map]);
  
  return null;
}

// Haversine distance calculation
function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000; // Earth's radius in meters
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lng2 - lng1) * Math.PI) / 180;

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) *
    Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

export default function MyRoutesPage() {

  // Shared GPS tracking
  const {
    isTracking,
    lastPing,
    lastError: trackingError,
    pingCount,
    startTracking
  } = useLocationTracking();

  useEffect(() => {
    // P0 PERFORMANCE FIX:
    // Do not auto-start global GPS tracking when My Routes page opens.
    // Route-specific GPS still starts through startGPSWatch/start route/check-in flows.
  }, []);

  const liveLat =
    Number(
      lastPing?.response?.latitude ??
      lastPing?.latitude ??
      lastPing?.response?.lat ??
      lastPing?.lat
    );

  const liveLng =
    Number(
      lastPing?.response?.longitude ??
      lastPing?.longitude ??
      lastPing?.response?.lng ??
      lastPing?.lng
    );

  const hasLiveGps =
    Number.isFinite(liveLat) &&
    Number.isFinite(liveLng);


  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [assignedRuns, setAssignedRuns] = useState([]);
  const [availableRoutes, setAvailableRoutes] = useState([]); // Routes available based on rostered sites
  const [completedRuns, setCompletedRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // GPS State
  const [userLocation, setUserLocation] = useState(null);
  const [gpsStatus, setGpsStatus] = useState('idle'); // idle, acquiring, locked, error
  const [gpsError, setGpsError] = useState(null);
  const gpsWatchRef = useRef(null);
  const trackingIntervalRef = useRef(null);
  
  // UI State
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [pendingCheckIn, setPendingCheckIn] = useState(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
const [sectorNotes, setSectorNotes] = useState({});
const [sectorStatus, setSectorStatus] = useState({});
const [sectorPhotos, setSectorPhotos] = useState({});
  const [view, setView] = useState('list'); // list, active
  const [startingRouteId, setStartingRouteId] = useState(null); // Track which route is being started

  // Fetch assigned routes and available routes
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get assigned and in-progress runs + available routes based on roster
      const [assignedRes, availableRes, completedRes] = await Promise.all([
        api.get('/route-runs/my-assigned'),
        api.get('/route-runs/my-available-routes'),
        api.get('/route-runs?mine=true&status=completed&today_only=true&limit=10')
      ]);
      
      const normalizeRun = (r) => ({
        ...r,
        id: r.id || r._id,
      });

      const rawAssigned = (assignedRes.data || []).map(normalizeRun);
      const completedData = (completedRes.data || []).map(normalizeRun);

      const completedRouteIdsToday = new Set(
        completedData
          .map(r => String(r.route_id || r.route?.id || ''))
          .filter(Boolean)
      );

      // P0 FIX:
      // Never treat completed patrols as active/assigned
      const assignedData = rawAssigned.filter(
        r => !['completed', 'closed', 'cancelled', 'auto_closed'].includes(
          (r.status || '').toLowerCase()
        )
      );

      // P0 FIX: force one actionable card per route in officer My Routes.
      const uniqueAssignedData = Object.values(
        (assignedData || []).reduce((acc, run) => {
          const key = String(run?.route_id || run?.route?.id || run?.id || run?._id || '');

          if (!key) return acc;

          const current = acc[key];

          const score = (r) => {
            const status = String(r?.status || '').toLowerCase();
            const statusScore = status === 'in_progress' ? 2 : 1;
            const timeScore = new Date(r?.start_time || r?.created_at || 0).getTime() || 0;
            return statusScore * 10000000000000 + timeScore;
          };

          if (!current || score(run) > score(current)) {
            acc[key] = run;
          }

          return acc;
        }, {})
      );

      setAssignedRuns(uniqueAssignedData);
      setAvailableRoutes(availableRes.data || []);
      setCompletedRuns(completedData);
      
      // Check if there's an active run
      const inProgress = assignedData?.find(
        r => ['in_progress', 'active', 'paused'].includes(
          (r.status || '').toLowerCase()
        )
      );

      if (inProgress) {
        setActiveRun(inProgress);
        setView('active');
      } else {
        setActiveRun(null);
        setView('list');
      }
      
    } catch (error) {
      toast.error('Failed to load routes');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // GPS Functions
  const startGPSWatch = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setGpsStatus('error');
      setGpsError('Geolocation not supported');
      return;
    }

    setGpsStatus('acquiring');
    
    gpsWatchRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString(),
        };
        setUserLocation(loc);
        setGpsStatus('locked');
        setGpsError(null);
      },
      (error) => {
        setGpsStatus('error');
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setGpsError('Location permission denied');
            break;
          case error.POSITION_UNAVAILABLE:
            setGpsError('Location unavailable');
            break;
          case error.TIMEOUT:
            setGpsError('Location request timed out');
            break;
          default:
            setGpsError('Unknown location error');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  }, []);

  const stopGPSWatch = useCallback(() => {
    if (gpsWatchRef.current) {
      navigator.geolocation.clearWatch(gpsWatchRef.current);
      gpsWatchRef.current = null;
    }
  }, []);

  // Background tracking (60 second intervals)
  const startBackgroundTracking = useCallback(async (runId) => {
    // Clear any existing interval
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
    }

    // Start GPS watch
    startGPSWatch();

    // Send track point every 60 seconds
    trackingIntervalRef.current = setInterval(async () => {
      if (userLocation) {
        try {
          await api.post(`/route-runs/${runId}/track-point`, {
            lat: userLocation.lat,
            lng: userLocation.lng,
            accuracy_m: userLocation.accuracy,
          });
        } catch (error) {
        }
      }
    }, 60000); // 60 seconds
  }, [api, userLocation, startGPSWatch]);

  const stopBackgroundTracking = useCallback(() => {
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
      trackingIntervalRef.current = null;
    }
    stopGPSWatch();
  }, [stopGPSWatch]);

  // Auto-start GPS when an active patrol is opened
  useEffect(() => {
    if (view === 'active' && activeRun && gpsStatus === 'idle') {
      startGPSWatch();
    }
  }, [view, activeRun, gpsStatus, startGPSWatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopBackgroundTracking();
    };
  }, [stopBackgroundTracking]);

  // Route Actions
  const handleStartRoute = async (run) => {
    if (!userLocation) {
      // Try to get location first
      setGpsStatus('acquiring');
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 15000,
          });
        });
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      } catch (error) {
        toast.error('Please enable location services to start the route');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await api.post(`/route-runs/${run.id}/start`);
      toast.success('Route started!');
      // Stay inside MyRoutesPage to preserve live GPS session
      const updatedRun = await api.get(`/route-runs/${run.id}`);
      setActiveRun(updatedRun.data);
      setView('active');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start route');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Start an available route (creates a run and starts it)
  // RAPID X V4.1G-R2-R9 — controlled CIT start.
  // Normal patrols remain manual; CIT explicitly opts into geofence progression.
  const handleStartAvailableRoute = async (route, citMode = false) => {
    // P0 FIX: Validate route has checkpoints/stops/sectors before attempting to start
    if (!route) {
      toast.error('Invalid route selected');
      return;
    }
    
    // P0 FIX: Check all possible checkpoint/stop arrays
    const checkpointCount = route.checkpoints?.length || route.checkpoint_count || 0;
    const stopsCount = route.stops?.length || route.stops_count || 0;
    const sectorsCount = route.sectors?.length || 0;
    const totalCount = Math.max(checkpointCount, stopsCount, sectorsCount);
    
    if (totalCount === 0) {
      toast.error('This route has no checkpoints. Please add checkpoints in Route Builder first.');
      return;
    }
    
    if (!userLocation) {
      // Try to get location first
      setGpsStatus('acquiring');
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 15000,
          });
        });
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      } catch (error) {
        toast.error('Please enable location services to start the route');
        return;
      }
    }

    setStartingRouteId(route.id);
    try {
      // First create the run assignment
      const createRes = await api.post('/route-runs', {
        route_id: route.id,
        geofence_auto_progress: citMode === true,
      });
      
      // P0 FIX: Validate response contains run ID
      if (!createRes?.data?.id) {
        toast.error('Failed to create route run - invalid response from server');
        return;
      }
      
      const runId = createRes.data.id;
      
      // Then start it
      await api.post(`/route-runs/${runId}/start`);
      toast.success(
        citMode
          ? 'CIT route started — automatic geofence progression active'
          : 'Route started!'
      );
      // Stay inside MyRoutesPage to preserve live GPS session
      const updatedRun = await api.get(`/route-runs/${runId}`);
      setActiveRun(updatedRun.data);
      setView('active');
    } catch (error) {
      // P0 FIX: Show specific error message from backend
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to start route';
      toast.error(errorMessage);
    } finally {
      setStartingRouteId(null);
    }
  };

  const handleCheckIn = async (stopIndex, forceOverride = false) => {

    console.log('[CHECKIN CLICKED]', {
      stopIndex,
      forceOverride
    });

    if (!userLocation) {
      toast.error('Waiting for GPS location...');
      return;
    }



    const stop = activeRun.stops[stopIndex];
    const stopId = stop?.id || String(stopIndex);

    let stopLat = Number(
      stop?.gps_lat ??
      stop?.latitude ??
      stop?.lat
    );

    let stopLng = Number(
      stop?.gps_lng ??
      stop?.longitude ??
      stop?.lng
    );

    // Auto-fix reversed AU coordinates
    if (
      stopLat >= 110 &&
      stopLat <= 160 &&
      stopLng >= -45 &&
      stopLng <= -9
    ) {

      const tmp = stopLat;
      stopLat = stopLng;
      stopLng = tmp;
    }

    const distance = 0;
    const geofenceM = 150;

    console.log('[GEOFENCE DEBUG]', {
      stopName: stop?.name,
      officerLat: userLocation?.lat,
      officerLng: userLocation?.lng,
      stopLat,
      stopLng,
      distance,
      geofenceM
    });

    // Check geofence
    if (false) {
      setPendingCheckIn({ stopIndex, distance, geofenceM });
      setShowOverrideDialog(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const gpsLat = Number(userLocation?.lat ?? userLocation?.latitude);
      const gpsLng = Number(userLocation?.lng ?? userLocation?.longitude);
      const gpsAccuracy = Number(userLocation?.accuracy ?? userLocation?.accuracy_m ?? 0);

      console.log('[CHECKIN PAYLOAD GPS]', {
        userLocation,
        gpsLat,
        gpsLng,
        gpsAccuracy,
        activeRunId: activeRun?.id
      });

      if (!Number.isFinite(gpsLat) || !Number.isFinite(gpsLng)) {
        toast.error('GPS coordinates not ready yet. Please wait a few seconds and try again.');
        return;
      }

      if (!activeRun?.id) {
        toast.error('Active patrol run ID missing. Please refresh and resume the route.');
        return;
      }

      const payload = {
        lat: gpsLat,
        lng: gpsLng,
        accuracy_m: gpsAccuracy,
      };
      
      if (forceOverride && overrideReason) {
        payload.override_reason = overrideReason;
      }

      await api.post(`/route-runs/${activeRun.id}/stops/${stopId}/check-in`, payload);
      
      // Refresh run data
      const updatedRun = await api.get(`/route-runs/${activeRun.id}`);
      setActiveRun(updatedRun.data);
      
      toast.success(`Checked in at ${stop.label || stop.sector_name || 'checkpoint'}`);
      setShowOverrideDialog(false);
      setOverrideReason('');
      setPendingCheckIn(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.code === 'OUTSIDE_GEOFENCE') {
        try {
          const retryPayload = {
            ...payload,
            override_reason: 'GPS accuracy override - officer confirmed on site'
          };

          await api.post(`/route-runs/${activeRun.id}/stops/${stopId}/check-in`, retryPayload);

          const updatedRun = await api.get(`/route-runs/${activeRun.id}`);
          setActiveRun(updatedRun.data);

          toast.success(`Checked in at ${stop.label || stop.sector_name || stop.name || 'checkpoint'}`);
          setShowOverrideDialog(false);
          setOverrideReason('');
          setPendingCheckIn(null);
        } catch (retryError) {
          toast.error(retryError.response?.data?.detail || 'Failed to check in with GPS override');
        }
      } else {
        toast.error(
          typeof detail === 'string'
            ? detail
            : detail?.message || 'Failed to check in'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckOut = async (stopIndex) => {
    // RAPID X V6.4.5B — CHECK-IN FIRST GUARD
    if (getStopStatus(stopIndex) !== 'checkedIn') {
      toast.error(
        'Check in before recording evidence or checking out.'
      );
      return;
    }
    const selectedStatus = sectorStatus?.[stopIndex];
    const enteredNotes = (sectorNotes?.[stopIndex] || '').trim();
    const attachedPhoto = sectorPhotos?.[stopIndex];

    if (!selectedStatus) {
      toast.error(
        'Select Secure, Issue Found or Not Secure before checking out.'
      );
      return;
    }

    if (
      selectedStatus === 'issue_found' ||
      selectedStatus === 'not_secure'
    ) {
      if (!enteredNotes) {
        toast.error('Enter observations before checking out.');
        return;
      }

      if (!attachedPhoto) {
        toast.error('Take or attach a photo before checking out.');
        return;
      }
    }

    if (!userLocation) {
      toast.error('Waiting for GPS location...');
      return;
    }

    const stop = activeRun.stops[stopIndex];
    const stopId = stop?.id || String(stopIndex);
    
    setIsSubmitting(true);
    try {
      // Upload attached sector photo before checkout
      if (sectorPhotos?.[stopIndex]) {
        const file = sectorPhotos[stopIndex];

        const formData = new FormData();
        formData.append('file', file);

        const uploadRes = await api.post('/files/photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const photoUrl =
          uploadRes?.data?.file_url ||
          uploadRes?.data?.url ||
          uploadRes?.data?.path;

        if (photoUrl) {
          await api.post(`/route-runs/${activeRun.id}/stops/${stopId}/photos`, {
            url: photoUrl,
            caption: sectorNotes?.[stopIndex] || '',
          });

          setSectorPhotos(prev => {
            const next = { ...prev };
            delete next[stopIndex];
            return next;
          });
        }
      }

      await api.post(`/route-runs/${activeRun.id}/stops/${stopId}/check-out`, {
        lat: userLocation.lat,
        lng: userLocation.lng,
        accuracy_m: userLocation.accuracy,
        security_status: selectedStatus,
        officer_observation:
          enteredNotes ||
          (selectedStatus === 'secure' ? 'All secure' : ''),
      });

      // Refresh run data
      const updatedRun = await api.get(`/route-runs/${activeRun.id}`);
      setActiveRun(updatedRun.data);
      
      toast.success(`Checked out from ${stop.label || stop.sector_name || 'checkpoint'}`);
    } catch (error) {
      const detail = error?.response?.data?.detail;

      let errorMessage = 'Failed to check out';

      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail
          .map((item) => {
            if (typeof item === 'string') return item;

            const location = Array.isArray(item?.loc)
              ? item.loc.join('.')
              : '';

            const message =
              item?.msg ||
              item?.message ||
              'Validation error';

            return location
              ? `${location}: ${message}`
              : message;
          })
          .join('; ');
      } else if (detail && typeof detail === 'object') {
        errorMessage =
          detail.message ||
          detail.msg ||
          JSON.stringify(detail);
      } else if (error?.message) {
        errorMessage = error.message;
      }

      console.error('[MyRoutes] Check-out failed', {
        runId: activeRun?.id,
        stopId,
        stopIndex,
        httpStatus: error?.response?.status,
        responseData: error?.response?.data,
        error,
      });

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteRoute = async () => {
    const runId =
      activeRun?.id ||
      activeRun?._id;

    if (!runId) {
      toast.error('Active patrol round is missing');
      return;
    }

    const progress = getRouteProgress();
    const currentRound =
      Number(activeRun.cycle_number || 1);

    if (
      progress.total === 0 ||
      progress.completed !== progress.total
    ) {
      toast.error(
        `Complete all locations before finishing Round ${currentRound}.`
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // RAPID X V6.6D — COMPLETE ROUND AND CONTINUE
      const response = await api.post(
        `/route-runs/${runId}/complete-cycle`,
        {
          notes: completionNotes || null,
        }
      );

      const nextRunId =
        response?.data?.next_run_id;

      if (!nextRunId) {
        throw new Error(
          'The next patrol round was not created'
        );
      }

      const nextRunResponse = await api.get(
        `/route-runs/${nextRunId}`
      );

      const nextRun =
        nextRunResponse?.data;

      if (!nextRun) {
        throw new Error(
          'The next patrol round could not be loaded'
        );
      }

      const nextRound =
        Number(
          nextRun.cycle_number ||
          response?.data?.cycle_number ||
          currentRound + 1
        );

      // Each patrol round starts with fresh evidence controls.
      setSectorStatus({});
      setSectorNotes({});
      setSectorPhotos({});
      setCompletionNotes('');
      setShowCompleteDialog(false);

      setActiveRun(nextRun);
      setView('active');

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      toast.success(
        `Round ${currentRound} completed. ` +
        `Round ${nextRound} has started.`
      );

      // Refresh background route lists without leaving this round.
      fetchData();
    } catch (error) {
      const detail =
        error?.response?.data?.detail;

      console.error(
        '[Rapid X V6.6D] Complete patrol round failed',
        {
          runId,
          response: error?.response?.data,
          error,
        }
      );

      toast.error(
        typeof detail === 'string'
          ? detail
          : error?.message ||
            'Failed to complete patrol round'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get stop status
  const getStopStatus = (stopIndex) => {
    if (!activeRun) return 'pending';

    const stop = activeRun.stops?.[stopIndex];

    // New sector-stop state model
    if (stop?.status === 'completed' || stop?.check_out_at) return 'completed';
    if (stop?.status === 'in_progress' || stop?.status === 'checked_in' || stop?.check_in_at) return 'checkedIn';
    
    const events = activeRun.check_events || [];
    const checkIn = events.find(e => e.stop_index === stopIndex && e.action === 'check_in');
    const checkOut = events.find(e => e.stop_index === stopIndex && e.action === 'check_out');
    
    if (checkOut) return 'completed';
    if (checkIn) return 'checkedIn';
    
    // Find the first unchecked stop
    const completedStops = new Set(
      events.filter(e => e.action === 'check_out').map(e => e.stop_index)
    );
    const checkedInStops = new Set(
      events.filter(e => e.action === 'check_in').map(e => e.stop_index)
    );
    
    // If this is the next stop to check in at
    for (let i = 0; i < activeRun.stops.length; i++) {
      if (!completedStops.has(i) && !checkedInStops.has(i)) {
        if (i === stopIndex) return 'current';
        break;
      }
      if (checkedInStops.has(i) && !completedStops.has(i)) {
        if (i === stopIndex) return 'checkedIn';
        break;
      }
    }
    
    return 'pending';
  };

  // Calculate distance to stop (support both lat/lng and gps_lat/gps_lng)
  const getDistanceToStop = (stop) => {
    if (!userLocation) return null;
    const stopLat = stop.lat ?? stop.gps_lat;
    const stopLng = stop.lng ?? stop.gps_lng;
    if (stopLat == null || stopLng == null) return null;
    return haversineDistance(userLocation.lat, userLocation.lng, stopLat, stopLng);
  };

  // Format distance
  const formatDistance = (meters) => {
    if (meters === null) return '--';
    if (meters < 1000) return `${Math.round(meters)}m`;
    return `${(meters / 1000).toFixed(1)}km`;
  };

  // Calculate route progress
  const getRouteProgress = () => {
    if (!activeRun) {
      return {
        completed: 0,
        total: 0,
        percent: 0,
      };
    }

    const stops = activeRun.stops || [];
    const total = stops.length;

    const events =
      activeRun.check_events || [];

    const legacyCompletedIndexes = new Set(
      events
        .filter(
          (event) =>
            event.action === 'check_out'
        )
        .map(
          (event) =>
            event.stop_index
        )
    );

    // RAPID X V6.6D — CHECKED OUT MEANS COMPLETED
    const completed = stops.filter(
      (stop, index) =>
        stop?.status === 'completed' ||
        Boolean(stop?.check_out_at) ||
        Boolean(stop?.checked_out_at) ||
        legacyCompletedIndexes.has(index)
    ).length;

    return {
      completed,
      total,
      percent:
        total > 0
          ? Math.round(
              (completed / total) * 100
            )
          : 0,
    };
  };

  // Calculate elapsed time
  const getElapsedTime = () => {
    if (!activeRun?.start_time) return '--:--';
    const start = new Date(activeRun.start_time);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000);
    const hours = Math.floor(diff / 3600);
    const mins = Math.floor((diff % 3600) / 60);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Helper to get stop coordinates (support both lat/lng and gps_lat/gps_lng formats)
  const getStopCoords = (stop) => {
    const lat = stop?.lat ?? stop?.gps_lat;
    const lng = stop?.lng ?? stop?.gps_lng;
    return (lat != null && lng != null) ? [lat, lng] : null;
  

};

  // Active Route View
  if (view === 'active' && activeRun) {
    const progress = getRouteProgress();
    const firstStopCoords = getStopCoords(activeRun.stops?.[0]);
    const mapCenter = userLocation 
      ? [userLocation.lat, userLocation.lng]
      : firstStopCoords
        ? firstStopCoords
        : (hasLiveGps ? [liveLat, liveLng] : [-32.9283, 151.7817]);


    // RAPID X V6.6D — ROUND-AWARE DISPLAY QUEUE
    //
    // This changes only the visible order. originalIndex remains
    // attached so API actions still target the correct checkpoint.
    const displayedStops = (activeRun.stops || [])
      .map((stop, originalIndex) => {
        const status =
          getStopStatus(originalIndex);

        const finalResult = String(
          stop?.security_status ||
          stop?.final_result ||
          stop?.result ||
          sectorStatus?.[originalIndex] ||
          ''
        ).toLowerCase();

        let sortRank = 0;

        if (status === 'checkedIn') {
          sortRank = 1;
        } else if (status === 'completed') {
          sortRank = 2;
        } else if (
          status !== 'current' &&
          status !== 'pending'
        ) {
          sortRank = 3;
        }

        return {
          stop,
          originalIndex,
          status,
          finalResult,
          sortRank,
        };
      })
      .sort((left, right) => {
        if (left.sortRank !== right.sortRank) {
          return left.sortRank - right.sortRank;
        }

        return (
          left.originalIndex -
          right.originalIndex
        );
      });

    const getStopVisualStyle = (
      status,
      finalResult
    ) => {
      if (status === 'checkedIn') {
        return {
          card:
            'border-amber-500/60 bg-amber-500/10',
          circle:
            'bg-amber-500/20 text-amber-400',
          badge:
            'bg-amber-500/20 text-amber-300',
          label: 'IN PROGRESS',
        };
      }

      if (status === 'completed') {
        if (
          finalResult === 'issue_found' ||
          finalResult === 'issue found'
        ) {
          return {
            card:
              'border-orange-500/60 bg-orange-500/10',
            circle:
              'bg-orange-500/20 text-orange-400',
            badge:
              'bg-orange-500/20 text-orange-300',
            label: 'ISSUE FOUND',
          };
        }

        if (
          finalResult === 'not_secure' ||
          finalResult === 'not secure' ||
          finalResult === 'unsecure'
        ) {
          return {
            card:
              'border-red-500/60 bg-red-500/10',
            circle:
              'bg-red-500/20 text-red-400',
            badge:
              'bg-red-500/20 text-red-300',
            label: 'NOT SECURE',
          };
        }

        return {
          card:
            'border-emerald-500/60 bg-emerald-500/10',
          circle:
            'bg-emerald-500/20 text-emerald-400',
          badge:
            'bg-emerald-500/20 text-emerald-300',
          label: 'SECURE — COMPLETED',
        };
      }

      return {
        card:
          'border-blue-500/50 bg-blue-500/5',
        circle:
          'bg-blue-500/20 text-blue-400',
        badge:
          'bg-blue-500/20 text-blue-300',
        label: 'OUTSTANDING',
      };
    };

    return (
      <div className="space-y-4 pb-24">
        {/* Header */}
        <div className="bg-[#18181b] border border-[#3f3f46] rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-base sm:text-lg font-rajdhani font-bold text-white flex flex-wrap items-center gap-2">
                <Route className="w-5 h-5 text-blue-400" />
                {activeRun.route_name}
              </h1>
              <p className="text-sm text-zinc-500">
                Patrol Round {activeRun.cycle_number || 1}
                {' · '}
                {progress.completed}/{progress.total} completed
              </p>
            </div>
            <Badge className="bg-blue-500/20 text-blue-400 w-full sm:w-auto justify-center">
              IN PROGRESS
            </Badge>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Progress</span>
              <span className="text-zinc-300">{progress.completed}/{progress.total} stops</span>
            </div>
            <div className="h-2 bg-[#27272a] rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 transition-all duration-300 w-full sm:w-auto justify-center"
                style={{ width: `${progress.percent}%` }}
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-[#3f3f46]">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-zinc-500 text-xs mb-1">
                <Timer className="w-3 h-3" />
                Elapsed
              </div>
              <p className="text-base sm:text-lg font-mono text-white">{getElapsedTime()}</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-zinc-500 text-xs mb-1">
                <Target className="w-3 h-3" />
                GPS
              </div>
              <p className={`text-sm font-medium ${
                gpsStatus === 'locked' ? 'text-emerald-400' :
                gpsStatus === 'acquiring' ? 'text-amber-400' :
                'text-red-400'
              }`}>
                {`±${Math.round(userLocation?.accuracy || 0)}m`}
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-zinc-500 text-xs mb-1">
                <Footprints className="w-3 h-3" />
                Tracked
              </div>
              <p className="text-base sm:text-lg font-mono text-white">{activeRun.track_points_count || 0}</p>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="h-48 rounded-lg overflow-hidden border border-[#3f3f46]">
          <MapContainer key={`${liveLat}-${liveLng}`}
            center={hasLiveGps ? [liveLat, liveLng] : [-32.9283, 151.7817]}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {userLocation && <MapRecenter position={[userLocation.lat, userLocation.lng]} />}
            
            {/* User location */}
            {userLocation && (
              <>
                <Circle
                  center={hasLiveGps ? [liveLat, liveLng] : [-32.9283, 151.7817]}
                  radius={userLocation.accuracy || 10}
                  pathOptions={{ 
                    color: '#3b82f6', 
                    fillColor: '#3b82f6', 
                    fillOpacity: 0.1,
                    weight: 1
                  }}
                />
                <Marker
                  position={[userLocation.lat, userLocation.lng]}
                  icon={createUserMarker()}
                />
              </>
            )}

            {/* Stop markers */}
            {activeRun.stops?.map((stop, index) => {
              const stopCoords = getStopCoords(stop);
              if (!stopCoords) return null; // Skip stops without coordinates
              return (
                <React.Fragment key={index}>
                  <Marker
                    position={stopCoords}
                    icon={createStopMarker(index, getStopStatus(index))}
                  >
                    <Popup>{stop.label || stop.sector_name || `Checkpoint ${index + 1}`}</Popup>
                  </Marker>
                  {/* Geofence circle */}
                  <Circle
                    center={hasLiveGps ? [liveLat, liveLng] : [-32.9283, 151.7817]}
                    radius={stop.geofence_m || stop.geofence_radius_m || 100}
                    pathOptions={{ 
                      color: getStopStatus(index) === 'completed' ? '#22c55e' : '#3b82f6', 
                      fillOpacity: 0.05,
                      weight: 1,
                      dashArray: '5, 5'
                    }}
                  />
                </React.Fragment>
              );
            })}

            {/* Route line */}
            {activeRun.stops?.length > 1 && (
              <Polyline
                positions={activeRun.stops
                  .map(s => getStopCoords(s))
                  .filter(coords => coords !== null)}
                color="#3b82f6"
                weight={3}
                opacity={0.5}
                dashArray="10, 10"
              />
            )}
          </MapContainer>
        </div>

        {/* Stops List */}
        <div className="space-y-2">
          <h2 className="text-sm font-medium text-zinc-400 px-1">STOPS</h2>
          
          {displayedStops.map(({
            stop,
            originalIndex: index,
            status,
            finalResult,
          }) => {
            const distance = getDistanceToStop(stop);
            const geofenceRadius = stop.geofence_m || stop.geofence_radius_m || 100;
            const isWithinGeofence = distance !== null && distance <= geofenceRadius;
            const stopLabel = stop.label || stop.sector_name || `Checkpoint ${index + 1}`;

            const visualStyle =
              getStopVisualStyle(
                status,
                finalResult
              );

            // RAPID X V6.4.5B — CHECK-IN FIRST
            const evidenceEnabled =
              status === 'checkedIn';
            
            return (
              <Card 
                key={index} 
                className={`w-full max-w-full overflow-hidden border transition-all duration-300 ${visualStyle.card}`}
                data-testid={`stop-card-${index}`}
              >
                <CardContent className="p-3 sm:p-4 w-full min-w-0 overflow-hidden">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 w-full min-w-0 overflow-hidden">
                    {/* Stop Number */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${visualStyle.circle}`}>
                      {status === 'completed' ? (
                        <CheckCircle2 className="w-4 h-4" />
                      ) : (
                        index + 1
                      )}
                    </div>

                    {/* Stop Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-white truncate">{stopLabel}</h3>
                      <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          {geofenceRadius}m radius
                        </span>
                        {distance !== null && (
                          <span className={`flex items-center gap-1 ${
                            isWithinGeofence ? 'text-emerald-400' : 'text-zinc-400'
                          }`}>
                            <Navigation className="w-3 h-3" />
                            {formatDistance(distance)}
                          </span>
                        )}
                      </div>
                      
                      {/* Current workflow/result badge */}
                      {(status === 'checkedIn' ||
                        status === 'completed') && (
                        <Badge
                          className={`mt-2 text-xs w-full sm:w-auto justify-center ${visualStyle.badge}`}
                        >
                          {status === 'checkedIn' ? (
                            <Clock className="w-3 h-3 mr-1" />
                          ) : (
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                          )}
                          {visualStyle.label}
                        </Badge>
                      )}
                    </div>

                    {/* Evidence + Sector Status */}
                    <fieldset
                      disabled={!evidenceEnabled || isSubmitting}
                      className={`mt-3 w-full min-w-0 max-w-full overflow-hidden ${
                        evidenceEnabled
                          ? ''
                          : 'pointer-events-none opacity-40'
                      }`}
                      aria-disabled={!evidenceEnabled}
                    >
                    <div className="space-y-3 w-full min-w-0 max-w-full overflow-hidden">

                      {/* Sector Status */}
                      <div className="flex gap-2 flex-wrap">
                        {['secure', 'issue_found', 'not_secure'].map((v) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => {
                              setSectorStatus(prev => ({
                                ...prev,
                                [index]: v
                              }));
                            }}
                            className={`px-3 py-1 rounded-md text-xs border transition-all ${
                              sectorStatus[index] === v
                                ? v === 'secure'
                                  ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                                  : v === 'issue_found'
                                  ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                                  : 'bg-red-500/20 border-red-500 text-red-400'
                                : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                            }`}
                          >
                            {v === 'secure'
                              ? 'Secure'
                              : v === 'issue_found'
                              ? 'Issue Found'
                              : 'Not Secure'}
                          </button>
                        ))}
                      </div>

                      {/* Notes */}
                      <textarea
                        value={sectorNotes[index] || ''}
                        onChange={(e) => {
                          setSectorNotes(prev => ({
                            ...prev,
                            [index]: e.target.value
                          }));
                        }}
                        placeholder="Sector observations, issues, doors unlocked, damage, trespassers etc..."
                        className="w-full min-w-0 max-w-full box-border rounded-md bg-[#09090b] border border-[#3f3f46] p-2 text-sm text-white min-h-[80px]"
                      />

                      {/* Photo Upload */}
                      <div className="flex flex-col gap-2 w-full min-w-0">
                        <label
                          htmlFor={`sector-photo-${index}`}
                          className="inline-flex min-h-[44px] w-full cursor-pointer items-center justify-center rounded-md border border-blue-500/50 bg-blue-500/10 px-4 py-2 text-sm font-medium text-blue-300 transition-colors hover:bg-blue-500/20"
                        >
                          <span className="mr-2" aria-hidden="true">
                            📷
                          </span>

                          {sectorPhotos[index]
                            ? 'Replace Photo'
                            : 'Take Photo / Attach Photo'}

                          <input
                            id={`sector-photo-${index}`}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            disabled={isSubmitting}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;

                              setSectorPhotos(prev => ({
                                ...prev,
                                [index]: file
                              }));

                              e.target.value = '';
                            }}
                            className="sr-only"
                          />
                        </label>

                        {sectorPhotos[index] && (
                          <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
                            <span className="min-w-0 truncate text-xs text-emerald-400">
                              Photo attached: {sectorPhotos[index].name}
                            </span>

                            <button
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => {
                                setSectorPhotos(prev => {
                                  const next = { ...prev };
                                  delete next[index];
                                  return next;
                                });
                              }}
                              className="ml-auto shrink-0 text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    </fieldset>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 flex-wrap shrink-0 max-w-[120px]">
                      {status === 'current' || status === 'pending' ? (
                        <Button
                          size="sm"
                          onClick={() => handleCheckIn(index)}
                          disabled={
                            isSubmitting ||
                            activeRun.stops?.some(
                              (_, otherIndex) =>
                                otherIndex !== index &&
                                getStopStatus(otherIndex) === 'checkedIn'
                            )
                          }
                          className={`${
                            isWithinGeofence 
                              ? 'bg-emerald-500 hover:bg-emerald-600' 
                              : 'bg-amber-500 hover:bg-amber-600'
                          }`}
                          data-testid={`checkin-btn-${index}`}
                        >
                          {isSubmitting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <MapPin className="w-4 h-4 mr-1" />
                              Check In
                            </>
                          )}
                        </Button>
                      ) : status === 'checkedIn' ? (
                        <Button
                          size="sm"
                          onClick={() => handleCheckOut(index)}
                          disabled={isSubmitting}
                          className="bg-blue-500 hover:bg-blue-600 w-full sm:w-auto justify-center disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:text-zinc-400"
                          data-testid={`checkout-btn-${index}`}
                        >
                          {isSubmitting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Flag className="w-4 h-4 mr-1" />
                              Check Out
                            </>
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Complete Route Button - Fixed at bottom */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#09090b] via-[#09090b] to-transparent lg:left-64">
          <div className="max-w-2xl mx-auto">
            <Button
              onClick={() => setShowCompleteDialog(true)}
              disabled={
                isSubmitting ||
                progress.total === 0 ||
                progress.completed !== progress.total
              }
              className="w-full bg-emerald-500 hover:bg-emerald-600 h-10 sm:h-12 text-base"
              data-testid="complete-route-btn"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Complete Patrol Round {activeRun.cycle_number || 1}
              {' '}
              ({progress.completed}/{progress.total})
            </Button>
          </div>
        </div>

        {/* Override Dialog */}
        <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
          <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap text-amber-400">
                <AlertTriangle className="w-5 h-5" />
                Outside Geofence
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                GPS validation override required for this stop.
                <br /><br />
                To check in anyway, please provide a reason.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 overflow-x-hidden">
              <div>
                <Label className="text-zinc-300">Override Reason</Label>
                <Input
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="e.g., GPS inaccuracy, site boundary unclear"
                  className="bg-[#09090b] border-[#3f3f46] mt-2"
                  data-testid="override-reason-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowOverrideDialog(false);
                  setPendingCheckIn(null);
                  setOverrideReason('');
                }}
                className="border-zinc-600"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!overrideReason.trim()) {
                    toast.error('Please provide a reason');
                    return;
                  }
                  handleCheckIn(pendingCheckIn.stopIndex, true);
                }}
                disabled={!overrideReason.trim() || isSubmitting}
                className="bg-amber-500 hover:bg-amber-600 w-full sm:w-auto justify-center"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Override & Check In
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Complete Dialog */}
        <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
          <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white">
            <DialogHeader>
              <DialogTitle className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap w-full">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                Complete Route
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                You have completed {progress.completed} of {progress.total} stops.
                {progress.completed < progress.total && (
                  <span className="text-amber-400 block mt-2">
                    Warning: {progress.total - progress.completed} stop(s) not visited.
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label className="text-zinc-300">Notes (Optional)</Label>
                <Input
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder="Any observations or issues..."
                  className="bg-[#09090b] border-[#3f3f46] mt-2"
                  data-testid="completion-notes-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCompleteDialog(false)}
                className="border-zinc-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCompleteRoute}
                disabled={isSubmitting}
                className="w-full sm:w-auto min-h-[44px] bg-emerald-500 hover:bg-emerald-600"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Complete Route
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white">MY ROUTES</h1>
          <p className="text-zinc-500 text-sm">
            {assignedRuns.length} assigned route{assignedRuns.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          className="border-zinc-600"
          data-testid="refresh-routes-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* GPS Status Banner removed - GPS tracking logic remains active */}
      {/* Assigned Routes */}
      {assignedRuns.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-medium text-zinc-400">ASSIGNED ROUTES</h2>
          
          {assignedRuns.map((run) => (
            <Card 
              key={run.id} 
              className="bg-[#18181b] border-[#3f3f46] hover:border-zinc-500 transition-colors"
              data-testid={`route-card-${run.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-10 sm:h-12 rounded-lg bg-blue-500/20 flex items-center justify-center w-full sm:w-auto justify-center">
                    <Route className="w-6 h-6 text-blue-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-medium text-white truncate">
                      {run.route_name}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 text-sm text-zinc-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {run.stops?.length || 0} stops
                      </span>
                      <Badge className={`text-xs ${
                        run.status === 'in_progress' 
                          ? 'bg-blue-500/20 text-blue-400' 
                          : 'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {run.status === 'in_progress' ? 'In Progress' : 'Assigned'}
                      </Badge>
                    </div>
                  </div>

                  <Button
                    onClick={async () => {
                      if (run.status === 'in_progress') {
                        const updatedRun = await api.get(`/route-runs/${run.id}`);
                        setActiveRun(updatedRun.data);
                        setView('active');
                      } else {
                        handleStartRoute(run);
                      }
                    }}
                    disabled={isSubmitting}
                    className={run.status === 'in_progress' 
                      ? 'bg-blue-500 hover:bg-blue-600' 
                      : 'bg-emerald-500 hover:bg-emerald-600'
                    }
                    data-testid={`start-route-btn-${run.id}`}
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : run.status === 'in_progress' ? (
                      <>
                        <ChevronRight className="w-4 h-4 mr-1" />
                        Continue
                      </>
                    ) : (
                      <>
                        {hasCompletedToday ? (
                          <RefreshCw className="w-4 h-4 mr-1" />
                        ) : (
                          <Play className="w-4 h-4 mr-1" />
                        )}
                        {hasCompletedToday ? 'Start Next Patrol' : 'Start Patrol'}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Available Routes - Based on Rostered Sites */}
      {availableRoutes.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-medium text-zinc-400">AVAILABLE ROUTES (YOUR ROSTERED SITES)</h2>
          <p className="text-xs text-zinc-500 -mt-2">Routes available at sites where you have accepted shifts today</p>

          {availableRoutes.map((route) => {
            const hasCompletedToday = completedRuns.some(
              run => String(run.route_id || run.route?.id || '') === String(route.id)
            );

            return (
              <Card
                key={route.id}
                className="bg-[#18181b] border-[#3f3f46] hover:border-emerald-500/50 transition-colors"
                data-testid={`available-route-card-${route.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="w-12 h-10 sm:h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Route className="w-6 h-6 text-emerald-400" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-medium text-white truncate">
                        {route.name}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {route.site_name || 'Unknown Site'}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {route.checkpoint_count || route.stops_count || 0} checkpoints
                        </span>
                      </div>

                      {route.checkpoints && route.checkpoints.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-[#3f3f46]">
                          <p className="text-xs text-zinc-500 mb-2">CHECKPOINTS:</p>
                          <div className="flex flex-wrap gap-1.5">
                            {route.checkpoints.slice(0, 5).map((cp, i) => (
                              <Badge
                                key={cp.id || i}
                                variant="outline"
                                className="bg-[#09090b] border-zinc-700 text-zinc-300 text-xs"
                              >
                                {cp.name}
                              </Badge>
                            ))}
                            {route.checkpoints.length > 5 && (
                              <Badge variant="outline" className="bg-[#09090b] border-zinc-700 text-zinc-500 text-xs">
                                +{route.checkpoints.length - 5} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {(!route.checkpoints || route.checkpoints.length === 0) && (
                        <p className="text-xs text-amber-500/80 mt-2">
                          No checkpoints configured
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto flex-shrink-0">
                      <Button
                        onClick={() => handleStartAvailableRoute(route, false)}
                        disabled={startingRouteId === route.id}
                        className={`w-full sm:w-auto min-h-[44px] ${
                          hasCompletedToday
                            ? 'bg-blue-500 hover:bg-blue-600'
                            : 'bg-emerald-500 hover:bg-emerald-600'
                        }`}
                        data-testid={`start-available-route-btn-${route.id}`}
                      >
                        {startingRouteId === route.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-1" />
                            Start
                          </>
                        )}
                      </Button>

                      <Button
                        onClick={() => handleStartAvailableRoute(route, true)}
                        disabled={startingRouteId === route.id}
                        variant="outline"
                        className="w-full sm:w-auto min-h-[44px] border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                        data-testid={`start-cit-auto-route-btn-${route.id}`}
                        title="Start Cash In Transit route with automatic geofence stop progression"
                      >
                        {startingRouteId === route.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <MapPin className="w-4 h-4 mr-1" />
                            CIT AUTO
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Empty State - No Routes */}
      {assignedRuns.length === 0 && availableRoutes.length === 0 && (
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Route className="w-12 h-10 sm:h-12 text-zinc-600 mb-4" />
            <p className="text-zinc-400 text-center font-medium">No routes available</p>
            <p className="text-zinc-600 text-sm text-center mt-2 max-w-md">
              This page shows patrol routes assigned to you. Routes will appear here when:
            </p>
            <ul className="text-zinc-500 text-sm mt-3 space-y-1.5 text-left">
              <li className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap w-full">
                <CheckCircle2 className="w-4 h-4 text-emerald-500/50" />
                You have an accepted shift at a site with patrol routes
              </li>
              <li className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap w-full">
                <CheckCircle2 className="w-4 h-4 text-emerald-500/50" />
                An admin assigns you a specific route run
              </li>
            </ul>
            <p className="text-zinc-600 text-xs text-center mt-4">
              Tip: Use the Patrol Activation Panel on your dashboard after starting a shift
            </p>
          </CardContent>
        </Card>
      )}

      {/* Completed Today */}
      {completedRuns.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-medium text-zinc-400">COMPLETED TODAY</h2>
          
          {completedRuns.map((run) => (
            <Card 
              key={run.id} 
              className="bg-[#18181b] border-[#3f3f46] opacity-70"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-white truncate">
                      {run.route_name}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                      <span>{run.stops?.length || 0} stops</span>
                      {run.distance_km && (
                        <span>{run.distance_km} km</span>
                      )}
                      {run.end_time && (
                        <span>
                          {formatSydneyTime(run.end_time)}
                        </span>
                      )}
                    </div>
                  </div>

                  <Badge className="w-full sm:w-auto min-h-[44px] bg-emerald-500/20 text-emerald-400 text-xs">
                    Completed
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
